import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Flex,
  Button,
  Avatar,
  Container,
  HStack,
  IconButton,
  Stack,
} from '@chakra-ui/react';
import { FiMenu, FiX, FiLogOut, FiSettings } from 'react-icons/fi';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useColorModeValue } from '../hooks/useColorModeValue';
import SettingsDialog from './SettingsDialog';
import LanguageSwitcher from './LanguageSwitcher';

const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Button
      {...{as: RouterLink, to} as any}
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'brand' : 'gray'}
      size="sm"
    >
      {children}
    </Button>
  );
};

import { NeurevLogo } from './NeurevLogo';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!isAuthenticated) {
    return (
      <Box bg={bg} borderBottom="1px" borderColor={borderColor} position="sticky" top={0} zIndex={1000}>
        <Container maxW="7xl">
          <Flex h={20} alignItems="center" justifyContent="space-between">
            <Box {...{as: RouterLink, to: "/"} as any} _hover={{ opacity: 0.8 }}>
              <NeurevLogo height={70} />
            </Box>
            <HStack gap={4}>
              <LanguageSwitcher />
              <Button {...{as: RouterLink, to: "/login"} as any} variant="ghost" size="sm">
                {t('nav.login')}
              </Button>
              <Button {...{as: RouterLink, to: "/register"} as any} colorScheme="brand" size="sm">
                {t('nav.register')}
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bg} borderBottom="1px" borderColor={borderColor} position="sticky" top={0} zIndex={1000}>
      <Container maxW="7xl">
        <Flex h={20} alignItems="center" justifyContent="space-between">
          <IconButton
            size="md"
            aria-label={t('nav.openMenu')}
            display={{ md: 'none' }}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <FiX /> : <FiMenu />}
          </IconButton>
          
          <HStack gap={8} alignItems="center">
            <Box {...{as: RouterLink, to: "/"} as any} _hover={{ opacity: 0.8 }}>
              <NeurevLogo height={70} />
            </Box>
            
            <HStack as="nav" gap={4} display={{ base: 'none', md: 'flex' }}>
              <NavLink to="/dashboard">{t('nav.dashboard')}</NavLink>
              <NavLink to="/create-review">{t('nav.createReview')}</NavLink>
              <NavLink to="/reviews">{t('nav.reviews')}</NavLink>
              <NavLink to="/standards">{t('nav.standards')}</NavLink>
              {user?.is_superuser && (
                <NavLink to="/admin">{t('nav.admin')}</NavLink>
              )}
            </HStack>
          </HStack>
          
          <Flex alignItems="center" gap={2}>
            <LanguageSwitcher />
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label={t('nav.userMenu')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  borderRadius: '9999px',
                  outline: 'none',
                  display: 'block',
                  lineHeight: 0,
                }}
              >
                <Avatar.Root size="sm" style={{ pointerEvents: 'none' }}>
                  <Avatar.Fallback name={`${user?.first_name} ${user?.last_name}`} />
                </Avatar.Root>
              </button>

              {menuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '180px',
                    background: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0',
                    zIndex: 1500,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => { setMenuOpen(false); setSettingsOpen(true); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      color: '#1a202c',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f7fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <FiSettings size={16} /> {t('nav.settings')}
                  </button>
                  <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                  <button
                    onClick={() => { setMenuOpen(false); handleLogout(); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 14px',
                      width: '100%',
                      background: 'none',
                      border: 'none',
                      color: '#1a202c',
                      fontSize: '14px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f7fafc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <FiLogOut size={16} /> {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </Flex>
        </Flex>

        {isOpen ? (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as="nav" gap={4}>
              <NavLink to="/dashboard">{t('nav.dashboard')}</NavLink>
              <NavLink to="/create-review">{t('nav.createReview')}</NavLink>
              <NavLink to="/reviews">{t('nav.reviews')}</NavLink>
              <NavLink to="/standards">{t('nav.standards')}</NavLink>
              {user?.is_superuser && (
                <NavLink to="/admin">{t('nav.admin')}</NavLink>
              )}
            </Stack>
          </Box>
        ) : null}
      </Container>

      <SettingsDialog isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
};

export default Navbar;
